import React, { useMemo } from "react";
import CommonModal from "@/components/common/CommonModal";
import { API_URL } from "@/api/constant";
import { handleFormatAmount } from "@/utility/helper";
import {
  TrendingUp,
  TrendingDown,
  Layers,
  AlertCircle,
  Receipt,
  UserCheck,
} from "lucide-react";

interface EmployeeSalaryBreakupModalProps {
  show: boolean;
  onClose: () => void;
  user: any;
}

const isSummaryHead = (name: string): boolean => {
  const normalized = name.toLowerCase().trim().replace(/[-_]/g, " ");
  return (
    normalized === "gross salary" ||
    normalized === "gross" ||
    normalized === "gross pay" ||
    normalized === "net salary" ||
    normalized === "net" ||
    normalized === "net pay" ||
    normalized === "total deduction" ||
    normalized === "total deductions"
  );
};

const isDeductionHeadName = (name: string): boolean => {
  const normalized = name.toLowerCase().trim();
  return (
    normalized.includes("pf") ||
    normalized.includes("esi") ||
    normalized.includes("provident") ||
    normalized.includes("tax") ||
    normalized.includes("tds") ||
    normalized.includes("deduction") ||
    normalized.includes("advance") ||
    normalized.includes("loan") ||
    normalized.includes("insurance") ||
    normalized.includes("recovery")
  );
};

const EmployeeSalaryBreakupModal: React.FC<EmployeeSalaryBreakupModalProps> = ({
  show,
  onClose,
  user,
}) => {
  if (!user) return null;

  const {
    name,
    employeeCode,
    profilePhoto,
    skillType,
    category,
    status,
    // joiningDate,
    isSalaryCreated,
    salaries = [],
  } = user;

  const isConfigured = Boolean(isSalaryCreated || (salaries && salaries.length > 0));

  // Process and separate salary heads cleanly
  const { earnings, deductions, grossSalary, netSalary } = useMemo(() => {
    let gross: number | null = null;
    let net: number | null = null;

    const earnList: { name: string; amount: number }[] = [];
    const deductList: { name: string; amount: number }[] = [];

    salaries.forEach((head: any) => {
      const headName =
        head.salaryName ||
        head.branchSalaryHead?.salaryName ||
        head.branchSalaryHead?.salaryHead?.name ||
        head.name ||
        "";
      if (!headName) return;

      const rawAmount = head.amount;
      const numAmount = rawAmount !== null && rawAmount !== undefined && rawAmount !== "" ? Number(rawAmount) : 0;
      const amount = isNaN(numAmount) ? 0 : numAmount;

      const normalized = headName.toLowerCase().trim().replace(/[-_]/g, " ");

      if (normalized === "gross salary" || normalized === "gross" || normalized === "gross pay") {
        gross = amount;
        return;
      }

      if (normalized === "net salary" || normalized === "net" || normalized === "net pay") {
        net = amount;
        return;
      }

      if (isSummaryHead(headName)) {
        return;
      }

      const headType = head.type || head.branchSalaryHead?.salaryHead?.type;
      if (headType === "deduction" || isDeductionHeadName(headName)) {
        deductList.push({ name: headName, amount });
      } else {
        earnList.push({ name: headName, amount });
      }
    });

    const calculatedEarnTotal = earnList.reduce((acc, curr) => acc + curr.amount, 0);
    const calculatedDeductTotal = deductList.reduce((acc, curr) => acc + curr.amount, 0);

    const finalGross = gross !== null ? gross : calculatedEarnTotal;
    const finalNet = net !== null ? net : (finalGross - calculatedDeductTotal);

    return {
      earnings: earnList,
      deductions: deductList,
      grossSalary: finalGross,
      netSalary: finalNet,
    };
  }, [salaries]);

  return (
    <CommonModal
      show={show}
      onClose={onClose}
      title=""
      className="sm:max-w-4xl max-w-4xl w-full p-0 overflow-hidden"
    >
      <div className="flex flex-col max-h-[85vh] bg-slate-50/50">
        {/* ── 1. Executive Header: Profile & Status ─────────────────────────────── */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 p-5 sm:p-6 text-white shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {profilePhoto ? (
                <img
                  src={`${API_URL}/${profilePhoto}`}
                  alt={name}
                  className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl object-cover border-2 border-white/20 shadow-md shrink-0"
                />
              ) : (
                <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl bg-indigo-500/20 text-white font-bold text-xl flex items-center justify-center border border-white/20 shadow-inner shrink-0">
                  {name?.charAt(0)?.toUpperCase() || "E"}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white capitalize">
                    {name}
                  </h3>
                  <span className="bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 text-xs font-mono font-bold px-2.5 py-0.5 rounded-md">
                    {employeeCode || "N/A"}
                  </span>
                  <span
                    className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${isConfigured
                      ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/30"
                      : "bg-rose-500/20 text-rose-300 border-rose-400/30"
                      }`}
                  >
                    {isConfigured ? "Salary Configured" : "Not Configured"}
                  </span>
                </div>

                {/* <div className="text-xs text-slate-300 flex items-center gap-3 mt-1.5 flex-wrap">
                  {email && <span>{email}</span>}
                  {department?.name && (
                    <span className="flex items-center gap-1 text-slate-300">
                      <Building2 className="w-3.5 h-3.5 text-indigo-300" />
                      {department.name}
                    </span>
                  )}
                  {designation?.name && (
                    <span className="flex items-center gap-1 text-slate-300">
                      <Briefcase className="w-3.5 h-3.5 text-indigo-300" />
                      {designation.name}
                    </span>
                  )}
                </div> */}
              </div>
            </div>

            {/* Quick Gross/Net Badges in Header */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="bg-slate-800/80 border border-slate-700 rounded-2xl px-4 py-2 text-right">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 block">
                  Gross Salary
                </span>
                <span className="text-lg font-bold text-slate-100">
                  {handleFormatAmount(grossSalary)}
                </span>
              </div>
              <div className="bg-emerald-950/70 border border-emerald-500/30 rounded-2xl px-4 py-2 text-right">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400 block">
                  Net Salary
                </span>
                <span className="text-lg font-black text-emerald-400">
                  {handleFormatAmount(netSalary)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── 2. Modal Body ─────────────────────────────────────────────────── */}
        <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
          {/* Employee Meta Details Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {/* <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-2xs">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1">
                <Building2 className="w-3.5 h-3.5 text-indigo-600" />
                <span>Department</span>
              </div>
              <span className="text-xs font-bold text-slate-800 truncate block">
                {department?.name || "-"}
              </span>
            </div> */}

            {/* <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-2xs">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1">
                <Briefcase className="w-3.5 h-3.5 text-indigo-600" />
                <span>Designation</span>
              </div>
              <span className="text-xs font-bold text-slate-800 truncate block">
                {designation?.name || "-"}
              </span>
            </div> */}

            <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-2xs">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1">
                <Layers className="w-3.5 h-3.5 text-indigo-600" />
                <span>Category / Skill</span>
              </div>
              <span className="text-xs font-bold text-slate-800 truncate block">
                {skillType || category || "General"}
              </span>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-2xs">
              <div className="flex items-center gap-1.5 text-slate-500 text-xs mb-1">
                <UserCheck className="w-3.5 h-3.5 text-indigo-600" />
                <span>Employee Status</span>
              </div>
              <span className="text-xs font-bold text-emerald-700 capitalize block">
                {status || "Active"}
              </span>
            </div>
          </div>

          {/* Salary Breakdown: Earnings vs Deductions */}
          {salaries.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-xs">
              <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-500 mx-auto flex items-center justify-center mb-3">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h4 className="text-sm font-bold text-slate-800 mb-1">
                Salary Structure Not Configured
              </h4>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                This employee does not have any active salary heads or structure assigned in the salary configuration tab.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Earnings Column */}
                <div className="bg-white border border-emerald-200 rounded-2xl overflow-hidden shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="bg-emerald-50/80 px-4 py-3 border-b border-emerald-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <h4 className="text-xs sm:text-sm font-bold text-emerald-950 uppercase tracking-wider">
                          Earnings & Allowances
                        </h4>
                      </div>
                      <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                        {earnings.length} Heads
                      </span>
                    </div>

                    <div className="p-3 divide-y divide-slate-100">
                      {earnings.length === 0 ? (
                        <div className="text-center py-6 text-xs text-slate-400">
                          No earning heads assigned
                        </div>
                      ) : (
                        earnings?.map((head, idx) => (
                          <div
                            key={idx}
                            className="py-2.5 px-2 flex items-center justify-between text-xs hover:bg-slate-50/60 rounded-lg transition-colors"
                          >
                            <span className="font-semibold text-slate-800">
                              {head?.name || ""}
                            </span>
                            <span className="font-bold text-emerald-700 text-sm">
                              {handleFormatAmount(head.amount)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="bg-emerald-50/50 p-4 border-t border-emerald-200 flex items-center justify-between font-bold text-sm">
                    <span className="text-slate-800">Total Gross Salary:</span>
                    <span className="text-emerald-700 text-base font-black">
                      {handleFormatAmount(grossSalary)}
                    </span>
                  </div>
                </div>

                {/* Deductions Column */}
                <div className="bg-white border border-rose-200 rounded-2xl overflow-hidden shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="bg-rose-50/80 px-4 py-3 border-b border-rose-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-rose-600" />
                        <h4 className="text-xs sm:text-sm font-bold text-rose-950 uppercase tracking-wider">
                          Deductions
                        </h4>
                      </div>
                      <span className="text-[11px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full">
                        {deductions.length} Heads
                      </span>
                    </div>

                    <div className="p-3 divide-y divide-slate-100">
                      {deductions.length === 0 ? (
                        <div className="text-center py-6 text-xs text-slate-400">
                          No standard deductions configured
                        </div>
                      ) : (
                        deductions.map((head, idx) => (
                          <div
                            key={idx}
                            className="py-2.5 px-2 flex items-center justify-between text-xs hover:bg-slate-50/60 rounded-lg transition-colors"
                          >
                            <span className="font-semibold text-slate-800">
                              {head.name}
                            </span>
                            <span className="font-bold text-rose-600 text-sm">
                              - {handleFormatAmount(head.amount)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="bg-rose-50/50 p-4 border-t border-rose-200 flex items-center justify-between font-bold text-sm">
                    <span className="text-slate-800">Total Deductions:</span>
                    <span className="text-rose-600 text-base font-black">
                      - {handleFormatAmount(deductions.reduce((acc, curr) => acc + curr.amount, 0))}
                    </span>
                  </div>
                </div>
              </div>

              {/* Net Take-Home Calculation Banner */}
              <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-400/30 shrink-0">
                    <Receipt className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-xs text-slate-300 font-medium block">
                      Monthly Salary Structure Summary
                    </span>
                    <span className="text-xs sm:text-sm font-semibold text-slate-200">
                      Gross Salary ({handleFormatAmount(grossSalary)}) − Total Deductions ({handleFormatAmount(deductions.reduce((acc, curr) => acc + curr.amount, 0))})
                    </span>
                  </div>
                </div>

                <div className="text-left sm:text-right shrink-0">
                  <span className="text-xs text-emerald-300 font-medium block">
                    Net In-Hand Salary
                  </span>
                  <span className="text-2xl sm:text-3xl font-black text-emerald-400">
                    {handleFormatAmount(netSalary)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </CommonModal>
  );
};

export default EmployeeSalaryBreakupModal;
